import { css } from '@emotion/react'
import { CardActions } from '@mui/material'
import { SyntheticEvent, memo, useCallback } from 'react'
import { openMenuDialog } from '../../../actions/menu'
import { goToNewsFromModel } from '../../../actions/nav'
import { openNewsChatNotificationsDrawer } from '../../../app-state/modals/state.drawer'
import { isMfcNews } from '../../../mfcLogic/api/news'
import { isModel } from '../../../mfcLogic/userProperties/user'
import { printTime } from '../../../utilities/dateLocalization'
import i18n from '../../../utilities/i18n'
import ButtonDelete from '../../Buttons/ButtonDelete'
import ButtonSettings from '../../Buttons/ButtonSettings'
import { useModelStyles } from '../../Hooks/useModelColor'
import { useUserSelector } from '../../Hooks/useUser'
import BadgeAvatar from '../../common/BadgeAvatar'
import { getThemeSpacing } from '../../styles/appTheme'
import { palette } from '../../styles/colors'
import shape from '../../styles/shape'
import ProcessedMessage from '../Chat/common/ProcessedMessage'

const cursorStyle = css`
    cursor: pointer;
`
const timestampStyle = css`
    font-size: 0.675rem;
`
const cardTopLeftStyle = css`
    width: auto;
    justify-content: space-evenly;
`

const cardHeaderStyles = css({
    display: 'flex',
    flexFlow: 'row nowrap',
    width: '100%',
    alignItems: 'center',
    padding: getThemeSpacing(0.3),
})

const chatRowRootStyles = css({
    width: '100%',
    border: `3px solid ${palette.drawerDivider}`,
    margin: `${getThemeSpacing(0.5)} 0`,
    borderRadius: shape.borderRadius,
})

interface IChatRowHeader {
    userId: number
    username: string
    timeStamp: string | number
    showButtonSettings?: boolean
    showButtonDelete?: boolean
    onDelete?(): void
}

const ChatRowHeader = memo((props: IChatRowHeader) => {
    const {
        showButtonSettings = false,
        showButtonDelete = false,
        timeStamp,
        userId,
        username,
        onDelete,
    } = props

    const level = useUserSelector(userId, 'level')
    const online = useUserSelector(userId, 'isOnline')
    const broadcasting = useUserSelector(userId, 'isBroadcasting')
    const color = useUserSelector(userId, 'color')

    const isNews = isMfcNews({ id: userId })
    const isUserModel = isModel({ level })
    const isOffline = isNews ? true : online || true
    const isBroadcasting = isNews ? true : broadcasting || false
    const modelStyles = useModelStyles({ color })

    const openNews = useCallback(() => goToNewsFromModel(username), [username])

    const onOpenMenu = useCallback(
        (e: SyntheticEvent) => {
            e.stopPropagation()
            openMenuDialog(username, { hideNextPrev: true })
        },
        [username],
    )

    return (
        <div css={cardHeaderStyles}>
            <div css={cardHeaderStyles}>
                <span css={cursorStyle} onClick={onOpenMenu}>
                    {isUserModel ? (
                        <BadgeAvatar
                            size={40}
                            id={userId}
                            offline={isOffline}
                            broadcasting={isBroadcasting}
                        />
                    ) : (
                        <BadgeAvatar
                            size={40}
                            id={userId}
                            alt={username}
                            disableBadge={true}
                        />
                    )}
                </span>
                <div
                    css={{
                        flex: '1 1 100%',
                        paddingLeft: getThemeSpacing(0.8),
                    }}
                >
                    <div style={modelStyles}>
                        {username}{' '}
                        <u css={cursorStyle} onClick={openNews}>
                            News Feed
                        </u>
                    </div>
                    <div css={timestampStyle}>
                        {`${i18n.t('news.newsCell.at')} ${printTime(
                            new Date(timeStamp),
                        )}`}
                    </div>
                </div>
                <div css={[cardHeaderStyles, cardTopLeftStyle]}>
                    {showButtonSettings && (
                        <ButtonSettings
                            onClick={openNewsChatNotificationsDrawer}
                            size={0.7}
                            dim
                        />
                    )}
                    {showButtonDelete && (
                        <ButtonDelete onClick={onDelete} size={0.8} />
                    )}
                </div>
            </div>
            <div />
        </div>
    )
})

interface ChatRowProps {
    html?: boolean
    id: number
    images?: any[]
    news?: boolean
    onLoad?: any
    showChatRowActions?: boolean
    text?: string
    timestamp?: number
    username: string
}

const ChatRow = memo((props: ChatRowProps) => {
    const {
        timestamp,
        text,
        images,
        onLoad,
        showChatRowActions = false,
        html,
        id,
        username: FCSUsername,
    } = props

    const username = useUserSelector(id || FCSUsername, 'name')

    if (!username || !id) return <></>

    return (
        <div css={chatRowRootStyles}>
            <ChatRowHeader
                userId={id}
                timeStamp={timestamp}
                showButtonSettings={true}
                username={username}
            />
            <div
                css={{
                    padding: getThemeSpacing(0.8),
                }}
            >
                <p className="typography paragraph">
                    <ProcessedMessage
                        text={text}
                        images={images}
                        onLoad={onLoad}
                        html={html}
                    />
                </p>
            </div>
            {showChatRowActions && (
                <CardActions disableSpacing>
                    <ButtonSettings
                        onClick={openNewsChatNotificationsDrawer}
                        dim
                    />
                </CardActions>
            )}
        </div>
    )
})

export default ChatRow
